import { useRef, useReducer } from 'react';

/**
 * Imperative handle for ControlRenderer — mirrors useFormRendererRef pattern.
 *
 * The returned object identity NEVER changes across renders — safe to pass
 * as a prop without causing child re-renders. Getter properties read from
 * React state so the *consumer* re-renders when values change, but the
 * handle reference itself is stable.
 *
 * When the rendered block tree contains a form (e.g. BUILD_PARTICIPANT_FORM),
 * FormBlockRenderer calls `_setFormState` so the parent ComposeModal can wire
 * `actionForm`, `dirty`, and `actionLoading` to ModalSheet without a custom
 * modal component.
 *
 * @example
 * ```tsx
 * const controlRef = useControlRendererRef();
 *
 * <ModalSheet
 *   loading={controlRef.isLoading}
 *   actionForm={controlRef.formId}
 *   dirty={controlRef.isDirty}
 *   actionLoading={controlRef.isSubmitting}
 * >
 *   <ControlRenderer actionId="BUILD_X" params={{ id }} handle={controlRef} />
 * </ModalSheet>
 * ```
 */
export interface ControlRendererHandle {
  readonly isLoading: boolean;
  /** Populated when the rendered block tree contains a form. */
  readonly isDirty: boolean;
  readonly isSubmitting: boolean;
  readonly formId: string | undefined;
  /** @internal — called by ActionBlockRenderer to update loading state */
  _setLoading: (loading: boolean) => void;
  /** @internal — called by FormBlockRenderer to surface form state */
  _setFormState: (formId: string, isDirty: boolean, isSubmitting: boolean) => void;
}

interface RefState {
  isLoading: boolean;
  isDirty: boolean;
  isSubmitting: boolean;
  formId: string | undefined;
}

export function useControlRendererRef(): ControlRendererHandle {
  const [state, dispatch] = useReducer(
    (prev: RefState, next: Partial<RefState>): RefState => {
      const merged = { ...prev, ...next };
      if (
        merged.isLoading === prev.isLoading &&
        merged.isDirty === prev.isDirty &&
        merged.isSubmitting === prev.isSubmitting &&
        merged.formId === prev.formId
      ) return prev;
      return merged;
    },
    { isLoading: true, isDirty: false, isSubmitting: false, formId: undefined },
  );

  const stateRef = useRef(state);
  stateRef.current = state;

  const handle = useRef<ControlRendererHandle | null>(null);
  if (handle.current === null) {
    handle.current = {
      get isLoading() { return stateRef.current.isLoading; },
      get isDirty() { return stateRef.current.isDirty; },
      get isSubmitting() { return stateRef.current.isSubmitting; },
      get formId() { return stateRef.current.formId; },
      _setLoading: (loading: boolean) => dispatch({ isLoading: loading }),
      _setFormState: (formId: string, isDirty: boolean, isSubmitting: boolean) =>
        dispatch({ formId, isDirty, isSubmitting }),
    };
  }

  return handle.current;
}
